/**
 * Datenmodell der Pipeline-Dokumentation (CUR-1571).
 *
 * Die Seiten sind eine **Erzählung mit Belegen**, kein Dashboard. Deshalb ist
 * jede Zahl ein `PipelineFact` mit eigener `source` — dem Befehl oder der
 * Datei, aus der sie stammt. Eine Zahl ohne Herkunft laesst sich im Gespraech
 * nicht verteidigen, und `pipelines.test.ts` erzwingt die Herkunft, damit die
 * Regel nicht von Disziplin abhaengt.
 *
 * Sollen die Zahlen spaeter live kommen (`/admin/content-status` existiert
 * bereits, wird vom Admin nur noch nicht genutzt), tauscht man die Herkunft —
 * nicht das Layout.
 */

/** Eine Kennzahl mit Herkunft. `source` ist Pflicht, nicht Zierde. */
export interface PipelineFact {
  label: string;
  value: string;
  /** Kurze Einordnung — was die Zahl bedeutet, nicht was sie ist. */
  hint?: string;
  /** Befehl oder Datei, aus der die Zahl stammt. */
  source: string;
}

/** Eine Stufe der Strecke. `nr` ist Text, weil es "3a"/"3b" gibt. */
export interface PipelineStage {
  id: string;
  nr: string;
  title: string;
  /** Ein Satz: wozu diese Stufe da ist. */
  purpose: string;
  input: string;
  output: string;
  /** Skill, Skript oder npm-Befehl, der die Stufe ausfuehrt. */
  tool: string;
  /** Absaetze fuer die Detailansicht. */
  detail: string[];
  /** Was hier erfahrungsgemaess schiefgeht. */
  risk?: string;
  cost?: string;
}

/**
 * Ein Waechter. `mode` fuehrt benannte Werte statt eines Booleans, weil genau
 * der Unterschied zwischen "gegen Null" und "Ratchet" das Interessante ist:
 * ein Gate, das ab Tag 1 rot steht, wird binnen einer Woche abgeschaltet —
 * also faehrt man gegen eine Grundlinie und laesst sie nur sinken.
 */
export interface PipelineGate {
  name: string;
  command?: string;
  /** Die Zusage, die das Gate traegt. */
  promise: string;
  mode: GateMode;
  /** Aktueller Stand, z. B. "305/305" oder "Grundlinie 238". */
  current?: string;
  /** Wofuer das Gate blind ist — gehoert sichtbar dazu. */
  blindFor?: string;
}

export type GateMode = 'gegen-null' | 'ratchet' | 'manuell' | 'test';

export const GATE_MODE_LABEL: Record<GateMode, string> = {
  'gegen-null': 'gegen Null',
  ratchet: 'Ratchet',
  manuell: 'manuell',
  test: 'Test',
};

export interface PipelineArtifact {
  name: string;
  path: string;
  purpose: string;
}

/** Ein Vorfall und die Regel, die daraus wurde. */
export interface PipelineLesson {
  incident: string;
  rule: string;
  ticket?: string;
}

/** Bekannter Rueckstand — bewusst sichtbar, nicht weggelassen. */
export interface PipelineGap {
  topic: string;
  state: string;
  why: string;
}

export interface PipelineSource {
  label: string;
  path: string;
}

/** Wie man eine Zahl neu erhebt, wenn der Stand altert. */
export interface PipelineRefresh {
  what: string;
  command: string;
}

export interface PipelineDoc {
  slug: string;
  title: string;
  /** Die These in einem Satz — Ueberschrift der Erzaehlung. */
  claim: string;
  summary: string[];
  /** Stand der Zahlen, ISO-Datum. */
  asOf: string;
  facts: PipelineFact[];
  stages: PipelineStage[];
  gates: PipelineGate[];
  artifacts: PipelineArtifact[];
  costs: PipelineFact[];
  lessons: PipelineLesson[];
  gaps: PipelineGap[];
  sources: PipelineSource[];
  refresh: PipelineRefresh[];
}

/** Eine Strecke, die es gibt, aber hier noch nicht beschrieben ist. */
export interface PlannedPipeline {
  slug: string;
  title: string;
  teaser: string;
}
